import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { sortCzech } from "@ckgo2/shared";
import RichTextEditor from "@/components/editor/RichTextEditor";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  Save,
  Send,
  Calendar,
  Sparkles,
  Eye,
  Loader2,
  Check,
  X,
  Image,
  MapPin,
  Users,
  Wand2,
  FlaskConical,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { formatMoney } from "@/lib/format";

interface Trip {
  id: string;
  title: string;
  destination?: {
    name: string;
  };
  priceFrom: number;
  durationDays: number;
  featuredImage?: string;
}

interface Campaign {
  id: string;
  subject: string;
  previewText?: string;
  content?: string;
  tripIds: string[];
  status: "draft" | "scheduled" | "sent";
  scheduledFor?: string;
  aiGenerated?: boolean;
  createdAt: string;
}

interface GeneratedContent {
  subject: string;
  previewText: string;
  content: string;
}

interface Segment {
  id: string;
  name: string;
  description?: string;
  aiPrompt: string;
  customerCount: number;
  lastGeneratedAt?: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  sent: { label: i18n.t("newsletter.campaignEditor.statusSent"), color: "text-green-600" },
  scheduled: {
    label: i18n.t("newsletter.campaignEditor.statusScheduled"),
    color: "text-yellow-600",
  },
  draft: { label: i18n.t("newsletter.campaignEditor.statusDraft"), color: "text-gray-500" },
};

function CampaignHeader({
  isNew,
  campaignStatus,
  isSaving,
  isSent,
  sendPending,
  sendTestPending,
  onBack,
  onPreview,
  onSave,
  onSend,
  onSendTest,
}: {
  isNew: boolean;
  campaignStatus?: string;
  isSaving: boolean;
  isSent: boolean;
  sendPending: boolean;
  sendTestPending: boolean;
  onBack: () => void;
  onPreview: () => void;
  onSave: () => void;
  onSend: () => void;
  onSendTest: () => void;
}) {
  const { t } = useTranslation();
  const statusInfo = campaignStatus ? STATUS_LABELS[campaignStatus] || STATUS_LABELS.draft : null;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="btn-ghost btn-sm">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isNew
              ? t("newsletter.campaignEditor.titleNew")
              : t("newsletter.campaignEditor.titleEdit")}
          </h1>
          {statusInfo && (
            <span className={`mt-1 inline-block text-sm ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onPreview} className="btn-secondary">
          <Eye className="h-5 w-5" />
          {t("common.actions.preview")}
        </button>
        <button onClick={onSave} disabled={isSaving || isSent} className="btn-primary">
          {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          {t("common.actions.save")}
        </button>
        {!isNew && (
          <button onClick={onSendTest} disabled={sendTestPending} className="btn-secondary">
            {sendTestPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <FlaskConical className="h-5 w-5" />
            )}
            {t("newsletter.campaignEditor.sendTest")}
          </button>
        )}
        {!isNew && !isSent && (
          <button
            onClick={onSend}
            disabled={sendPending}
            className="btn-primary bg-green-600 hover:bg-green-700"
          >
            {sendPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
            {t("common.actions.send")}
          </button>
        )}
      </div>
    </div>
  );
}

function AiOptionsPanel({
  aiTone,
  aiTheme,
  generating,
  onToneChange,
  onThemeChange,
  onGenerate,
  onCancel,
}: {
  aiTone: string;
  aiTheme: string;
  generating: boolean;
  onToneChange: (v: "friendly" | "formal" | "excited") => void;
  onThemeChange: (v: string) => void;
  onGenerate: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 space-y-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("newsletter.campaignEditor.toneOfVoice")}
        </label>
        <div className="flex gap-2">
          {[
            { value: "friendly", label: t("newsletter.campaignEditor.toneFriendly") },
            { value: "formal", label: t("newsletter.campaignEditor.toneFormal") },
            { value: "excited", label: t("newsletter.campaignEditor.toneExcited") },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => onToneChange(option.value as "friendly" | "formal" | "excited")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                aiTone === option.value
                  ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                  : "bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("newsletter.campaignEditor.themeOptional")}
        </label>
        <input
          type="text"
          value={aiTheme}
          onChange={(e) => onThemeChange(e.target.value)}
          placeholder={t("newsletter.campaignEditor.themePlaceholder")}
          className="input"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-ghost">
          {t("common.actions.cancel")}
        </button>
        <button onClick={onGenerate} disabled={generating} className="btn-primary">
          {generating ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("newsletter.campaignEditor.generating")}
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              {t("newsletter.campaignEditor.generateContent")}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function CampaignMainContent({
  subject,
  previewText,
  content,
  isSent,
  isNew,
  scheduledFor,
  showAiOptions,
  aiTone,
  aiTheme,
  selectedTripIds,
  generatePending,
  sendPending,
  onSubjectChange,
  onPreviewTextChange,
  onContentChange,
  onScheduledForChange,
  onShowAiOptionsToggle,
  onAiToneChange,
  onAiThemeChange,
  onGenerate,
  onHideAiOptions,
  onScheduleSend,
}: {
  subject: string;
  previewText: string;
  content: string;
  isSent: boolean;
  isNew: boolean;
  scheduledFor: string;
  showAiOptions: boolean;
  aiTone: "friendly" | "formal" | "excited";
  aiTheme: string;
  selectedTripIds: string[];
  generatePending: boolean;
  sendPending: boolean;
  onSubjectChange: (v: string) => void;
  onPreviewTextChange: (v: string) => void;
  onContentChange: (v: string) => void;
  onScheduledForChange: (v: string) => void;
  onShowAiOptionsToggle: () => void;
  onAiToneChange: (v: "friendly" | "formal" | "excited") => void;
  onAiThemeChange: (v: string) => void;
  onGenerate: () => void;
  onHideAiOptions: () => void;
  onScheduleSend: (scheduled: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 lg:col-span-2">
      {/* Subject & Preview */}
      <div className="card space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("newsletter.campaignEditor.emailSubject")}
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder={t("newsletter.campaignEditor.subjectPlaceholder")}
            className="input"
            disabled={isSent}
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("newsletter.campaignEditor.previewTextLabel")}
          </label>
          <input
            type="text"
            value={previewText}
            onChange={(e) => onPreviewTextChange(e.target.value)}
            placeholder={t("newsletter.campaignEditor.previewTextPlaceholder")}
            className="input"
            maxLength={150}
            disabled={isSent}
          />
          <p className="mt-1 text-xs text-gray-500">
            {t("newsletter.campaignEditor.charCount", { n: previewText.length })}
          </p>
        </div>
      </div>

      {/* AI Generation */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">
              {t("newsletter.campaignEditor.aiGenerationTitle")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("newsletter.campaignEditor.aiGenerationHint")}
            </p>
          </div>
          <button
            onClick={onShowAiOptionsToggle}
            disabled={selectedTripIds.length === 0 || generatePending || isSent}
            className="btn-secondary"
          >
            {generatePending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5" />
            )}
            {t("newsletter.campaignEditor.generateWithAi")}
          </button>
        </div>

        {showAiOptions && (
          <AiOptionsPanel
            aiTone={aiTone}
            aiTheme={aiTheme}
            generating={generatePending}
            onToneChange={onAiToneChange}
            onThemeChange={onAiThemeChange}
            onGenerate={onGenerate}
            onCancel={onHideAiOptions}
          />
        )}
      </div>

      {/* Content Editor */}
      <div className="card">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("newsletter.campaignEditor.emailContent")}
        </label>
        {isSent ? (
          <div
            className="prose prose-sm dark:prose-invert max-w-none rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
          />
        ) : (
          <RichTextEditor
            content={content}
            onChange={onContentChange}
            placeholder={t("newsletter.campaignEditor.contentPlaceholder")}
          />
        )}
      </div>

      {/* Schedule */}
      {!isSent && !isNew && (
        <div className="card">
          <h3 className="mb-4 font-medium text-gray-900 dark:text-white">
            {t("newsletter.campaignEditor.scheduleSending")}
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => onScheduledForChange(e.target.value)}
                className="input"
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
            <button
              onClick={() => onScheduleSend(scheduledFor)}
              disabled={!scheduledFor || sendPending}
              className="btn-secondary"
            >
              <Calendar className="h-5 w-5" />
              {t("newsletter.campaignEditor.schedule")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentSection({
  isSent,
  selectedSegment,
  selectedSegmentId,
  showSegmentSelector,
  segments,
  onSelectorToggle,
  onSelect,
  onClear,
  onShowSelector,
}: {
  isSent: boolean;
  selectedSegment: Segment | undefined;
  selectedSegmentId: string | null;
  showSegmentSelector: boolean;
  segments: Segment[];
  onSelectorToggle: () => void;
  onSelect: (id: string) => void;
  onClear: () => void;
  onShowSelector: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-medium text-gray-900 dark:text-white">
          {t("newsletter.campaignEditor.targetAudience")}
        </h3>
        {!isSent && (
          <button onClick={onSelectorToggle} className="btn-ghost btn-sm">
            <Wand2 className="h-4 w-4" />
            {selectedSegment
              ? t("newsletter.campaignEditor.changeSegment")
              : t("newsletter.campaignEditor.selectSegment")}
          </button>
        )}
      </div>

      {selectedSegment ? (
        <div className="border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20 rounded-lg border p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-900 dark:text-primary-100 font-medium">
                {selectedSegment.name}
              </p>
              <p className="text-primary-700 dark:text-primary-300 text-sm">
                {t("newsletter.shared.customersCount", { count: selectedSegment.customerCount })}
              </p>
            </div>
            {!isSent && (
              <button
                title={t("common.actions.clear")}
                onClick={onClear}
                className="text-primary-600 hover:text-primary-800"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="text-primary-600 dark:text-primary-400 mt-2 text-xs">
            {selectedSegment.aiPrompt}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center dark:border-gray-600">
          <Users className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">
            {t("newsletter.campaignEditor.allSubscribers")}
          </p>
          <button
            onClick={onShowSelector}
            className="text-primary-600 mt-2 text-sm hover:underline"
          >
            {t("newsletter.campaignEditor.orSelectSegment")}
          </button>
        </div>
      )}

      {showSegmentSelector && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("newsletter.campaignEditor.selectSegmentColon")}
          </p>
          <div className="max-h-48 space-y-2 overflow-y-auto">
            {segments.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500">
                {t("newsletter.campaignEditor.noSegments")}{" "}
                <a href="/segments" className="text-primary-600 hover:underline">
                  {t("newsletter.campaignEditor.createNew")}
                </a>
              </p>
            ) : (
              segments.map((segment) => (
                <button
                  key={segment.id}
                  onClick={() => onSelect(segment.id)}
                  className={`flex w-full items-center justify-between rounded-lg border p-2 text-left transition-colors ${
                    selectedSegmentId === segment.id
                      ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                      : "border-gray-200 hover:border-gray-300 dark:border-gray-700"
                  }`}
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{segment.name}</p>
                    <p className="text-xs text-gray-500">{segment.aiPrompt}</p>
                  </div>
                  <span className="text-sm text-gray-500">{segment.customerCount}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TripSelectionSection({
  isSent,
  selectedTripIds,
  trips,
  onToggle,
}: {
  isSent: boolean;
  selectedTripIds: string[];
  trips: Trip[];
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h3 className="mb-4 font-medium text-gray-900 dark:text-white">
        {t("newsletter.campaignEditor.tripsInNewsletter", { n: selectedTripIds.length })}
      </h3>
      <div className="max-h-[500px] space-y-2 overflow-y-auto">
        {trips.map((trip) => {
          const isSelected = selectedTripIds.includes(trip.id);
          return (
            <button
              key={trip.id}
              onClick={() => onToggle(trip.id)}
              disabled={isSent}
              className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                isSelected
                  ? "border-primary-500 bg-primary-50 dark:border-primary-600 dark:bg-primary-900/20"
                  : "border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800"
              } ${isSent ? "cursor-not-allowed opacity-60" : ""}`}
            >
              <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                {trip.featuredImage ? (
                  <img
                    src={trip.featuredImage}
                    alt={trip.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Image className="h-5 w-5 text-gray-400" />
                  </div>
                )}
                {isSelected && (
                  <div className="bg-primary-500/80 absolute inset-0 flex items-center justify-center">
                    <Check className="h-5 w-5 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="truncate font-medium text-gray-900 dark:text-white">{trip.title}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <MapPin className="h-3 w-3" />
                  {trip.destination?.name || "—"}
                  <span>•</span>
                  {t("newsletter.campaignEditor.daysCount", { count: trip.durationDays })}
                  <span>•</span>
                  {formatMoney(trip.priceFrom ?? 0)}
                </div>
              </div>
            </button>
          );
        })}
        {trips.length === 0 && (
          <p className="py-8 text-center text-gray-500">
            {t("newsletter.campaignEditor.noActiveTrips")}
          </p>
        )}
      </div>
    </div>
  );
}

function CampaignPreviewModal({
  subject,
  previewText,
  content,
  onClose,
}: {
  subject: string;
  previewText: string;
  content: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 dark:bg-gray-900">
        <button
          title={t("common.actions.close")}
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
          {t("newsletter.campaignEditor.emailPreview")}
        </h2>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 border-b border-gray-200 pb-4 dark:border-gray-700">
            <p className="text-sm text-gray-500">{t("newsletter.campaignEditor.subjectColon")}</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {subject || t("newsletter.campaignEditor.noSubject")}
            </p>
            {previewText && (
              <>
                <p className="mt-2 text-sm text-gray-500">
                  {t("newsletter.campaignEditor.previewColon")}
                </p>
                <p className="text-gray-600 dark:text-gray-400">{previewText}</p>
              </>
            )}
          </div>
          <div
            className="prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                content || `<p>${t("newsletter.campaignEditor.emptyContent")}</p>`,
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}

function CampaignSidebar({
  isSent,
  selectedSegment,
  selectedSegmentId,
  showSegmentSelector,
  segments,
  selectedTripIds,
  trips,
  onSegmentSelectorToggle,
  onSegmentSelect,
  onSegmentClear,
  onShowSegmentSelector,
  onTripToggle,
}: {
  isSent: boolean;
  selectedSegment: Segment | undefined;
  selectedSegmentId: string | null;
  showSegmentSelector: boolean;
  segments: Segment[];
  selectedTripIds: string[];
  trips: Trip[];
  onSegmentSelectorToggle: () => void;
  onSegmentSelect: (id: string) => void;
  onSegmentClear: () => void;
  onShowSegmentSelector: () => void;
  onTripToggle: (id: string) => void;
}) {
  return (
    <div className="space-y-6">
      <SegmentSection
        isSent={isSent}
        selectedSegment={selectedSegment}
        selectedSegmentId={selectedSegmentId}
        showSegmentSelector={showSegmentSelector}
        segments={segments}
        onSelectorToggle={onSegmentSelectorToggle}
        onSelect={onSegmentSelect}
        onClear={onSegmentClear}
        onShowSelector={onShowSegmentSelector}
      />

      <TripSelectionSection
        isSent={isSent}
        selectedTripIds={selectedTripIds}
        trips={trips}
        onToggle={onTripToggle}
      />
    </div>
  );
}

export default function CampaignEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  // Form state
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [content, setContent] = useState("");
  const [selectedTripIds, setSelectedTripIds] = useState<string[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [scheduledFor, setScheduledFor] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showAiOptions, setShowAiOptions] = useState(false);
  const [showSegmentSelector, setShowSegmentSelector] = useState(false);
  const [aiTone, setAiTone] = useState<"friendly" | "formal" | "excited">("friendly");
  const [aiTheme, setAiTheme] = useState("");

  // Fetch campaign if editing
  const { data: campaignData, isLoading: loadingCampaign } = useQuery({
    queryKey: ["newsletter-campaign", id],
    queryFn: () => api.get<ApiResponse<Campaign>>(`/api/admin/newsletter/campaigns/${id}`),
    enabled: !isNew && !!id,
  });

  // Fetch trips for selection
  const { data: tripsData } = useQuery({
    queryKey: ["trips-for-newsletter"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips?status=active"),
  });

  // Fetch segments
  const { data: segmentsData } = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get<ApiResponse<Segment[]>>("/api/admin/segments"),
  });

  // Výběr zájezdů abecedně (`sortCzech`); admin seznam je řazený podle `createdAt`.
  const trips = sortCzech(tripsData?.data ?? [], (t) => t.title);
  const segments = segmentsData?.data || [];
  const campaign = campaignData?.data;
  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);

  // Populate form when campaign loads
  const lastSyncedCampaignId = useRef<string | null>(null);
  useEffect(() => {
    if (campaign && campaign.id !== lastSyncedCampaignId.current) {
      lastSyncedCampaignId.current = campaign.id;
      setSubject(campaign.subject || "");
      setPreviewText(campaign.previewText || "");
      setContent(campaign.content || "");
      setSelectedTripIds(campaign.tripIds || []);
      setScheduledFor(campaign.scheduledFor || "");
    }
  }, [campaign]);

  // Create campaign mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<Campaign>) =>
      api.post<ApiResponse<Campaign>>("/api/admin/newsletter/campaigns", data),
    onSuccess: (response) => {
      toast.success(t("newsletter.campaignEditor.toastCreated"));
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      navigate(`/newsletter/editor/${response.data.id}`);
    },
    onError: () => {
      toast.error(t("newsletter.campaignEditor.toastCreateFailed"));
    },
  });

  // Update campaign mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Campaign>) =>
      api.put<ApiResponse<Campaign>>(`/api/admin/newsletter/campaigns/${id}`, data),
    onSuccess: () => {
      toast.success(t("newsletter.campaignEditor.toastSaved"));
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaign", id] });
    },
    onError: () => {
      toast.error(t("newsletter.campaignEditor.toastSaveFailed"));
    },
  });

  // AI generation mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (isNew) {
        // First create the campaign, then generate
        const createResponse = await api.post<ApiResponse<Campaign>>(
          "/api/admin/newsletter/campaigns",
          { subject: "New campaign", tripIds: selectedTripIds },
        );
        const newId = createResponse.data?.id;

        if (!newId) {
          throw new Error(t("newsletter.campaignEditor.errorMissingId"));
        }

        const generateResponse = await api.post<
          ApiResponse<{ data: Campaign; generated: GeneratedContent }>
        >(`/api/admin/newsletter/campaigns/${newId}/generate`, {
          tripIds: selectedTripIds,
          tone: aiTone,
          theme: aiTheme || undefined,
        });
        return { ...generateResponse, campaignId: newId };
      } else {
        if (!id) {
          throw new Error(t("newsletter.campaignEditor.errorCampaignIdMissing"));
        }
        const response = await api.post<
          ApiResponse<{ data: Campaign; generated: GeneratedContent }>
        >(`/api/admin/newsletter/campaigns/${id}/generate`, {
          tripIds: selectedTripIds,
          tone: aiTone,
          theme: aiTheme || undefined,
        });
        return { ...response, campaignId: id };
      }
    },
    onSuccess: (response: { generated?: GeneratedContent; campaignId?: string }) => {
      const generated = response.generated;
      if (generated) {
        setSubject(generated.subject);
        setPreviewText(generated.previewText);
        setContent(generated.content);
      }
      setShowAiOptions(false);
      toast.success(t("newsletter.campaignEditor.toastGenerated"));
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      if (isNew && response.campaignId) {
        navigate(`/newsletter/editor/${response.campaignId}`, { replace: true });
      }
    },
    onError: (error: Error) => {
      const message = error?.message || t("newsletter.campaignEditor.toastGenerateFailed");
      toast.error(message);
    },
  });

  // Send/schedule mutation
  const sendMutation = useMutation({
    mutationFn: (data: { scheduledFor?: string }) =>
      api.post<ApiResponse<{ message: string; recipientCount: number }>>(
        `/api/admin/newsletter/campaigns/${id}/send`,
        data,
      ),
    onSuccess: (response) => {
      toast.success(response.data?.message || t("newsletter.campaignEditor.toastSent"));
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      navigate("/newsletter");
    },
    onError: () => {
      toast.error(t("newsletter.campaignEditor.toastSendFailed"));
    },
  });

  // Send ONE test copy to the logged-in user (or a typed address) — safe preview before the
  // real Send goes to the whole audience. Doesn't touch the audience or the campaign status.
  const sendTestMutation = useMutation({
    mutationFn: (email?: string) =>
      api.post<ApiResponse<{ message: string }>>(
        `/api/admin/newsletter/campaigns/${id}/send-test`,
        email ? { email } : {},
      ),
    onSuccess: (response) =>
      toast.success(response.data?.message || t("newsletter.campaignEditor.toastTestSent")),
    onError: () => toast.error(t("newsletter.campaignEditor.toastTestFailed")),
  });

  const handleSave = async () => {
    const data = {
      subject,
      previewText,
      content,
      tripIds: selectedTripIds,
      scheduledFor: scheduledFor || undefined,
    };

    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  const handleSendTest = () => {
    if (!id || isNew) {
      toast.error(t("newsletter.campaignEditor.saveFirst"));
      return;
    }
    const email = window.prompt(t("newsletter.campaignEditor.testPrompt"))?.trim();
    if (email === undefined) return; // cancelled
    sendTestMutation.mutate(email || undefined);
  };

  const handleSend = (scheduled?: string) => {
    if (!id || isNew) {
      toast.error(t("newsletter.campaignEditor.saveFirst"));
      return;
    }
    // datetime-local is local wall-clock time — the dispatcher cron compares UTC instants.
    sendMutation.mutate({
      scheduledFor: scheduled ? new Date(scheduled).toISOString() : undefined,
    });
  };

  const toggleTrip = (tripId: string) => {
    setSelectedTripIds((prev) =>
      prev.includes(tripId) ? prev.filter((id) => id !== tripId) : [...prev, tripId],
    );
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSent = campaign?.status === "sent";

  if (!isNew && loadingCampaign) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CampaignHeader
        isNew={isNew}
        campaignStatus={campaign?.status}
        isSaving={isSaving}
        isSent={isSent}
        sendPending={sendMutation.isPending}
        sendTestPending={sendTestMutation.isPending}
        onBack={() => navigate("/newsletter")}
        onPreview={() => setShowPreview(!showPreview)}
        onSave={handleSave}
        onSend={() => handleSend()}
        onSendTest={handleSendTest}
      />

      {isSent && (
        <div className="rounded-lg bg-yellow-50 p-4 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
          {t("newsletter.campaignEditor.alreadySent")}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <CampaignMainContent
          subject={subject}
          previewText={previewText}
          content={content}
          isSent={isSent}
          isNew={isNew}
          scheduledFor={scheduledFor}
          showAiOptions={showAiOptions}
          aiTone={aiTone}
          aiTheme={aiTheme}
          selectedTripIds={selectedTripIds}
          generatePending={generateMutation.isPending}
          sendPending={sendMutation.isPending}
          onSubjectChange={setSubject}
          onPreviewTextChange={setPreviewText}
          onContentChange={setContent}
          onScheduledForChange={setScheduledFor}
          onShowAiOptionsToggle={() => setShowAiOptions(!showAiOptions)}
          onAiToneChange={setAiTone}
          onAiThemeChange={setAiTheme}
          onGenerate={() => generateMutation.mutate()}
          onHideAiOptions={() => setShowAiOptions(false)}
          onScheduleSend={(scheduled) => handleSend(scheduled)}
        />

        <CampaignSidebar
          isSent={isSent}
          selectedSegment={selectedSegment}
          selectedSegmentId={selectedSegmentId}
          showSegmentSelector={showSegmentSelector}
          segments={segments}
          selectedTripIds={selectedTripIds}
          trips={trips}
          onSegmentSelectorToggle={() => setShowSegmentSelector(!showSegmentSelector)}
          onSegmentSelect={(id) => {
            setSelectedSegmentId(id);
            setShowSegmentSelector(false);
          }}
          onSegmentClear={() => setSelectedSegmentId(null)}
          onShowSegmentSelector={() => setShowSegmentSelector(true)}
          onTripToggle={toggleTrip}
        />
      </div>

      {showPreview && (
        <CampaignPreviewModal
          subject={subject}
          previewText={previewText}
          content={content}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
